import React from "react";
import { Bar, BarChart, CartesianGrid, ComposedChart, Legend, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { SeriesTooltip } from "../admin/components/primitives";
import { money, moneyShort, toNumber } from "../admin/format";

const MARGIN = { top: 8, right: 12, left: 4, bottom: 0 };
const LEGEND_STYLE = { fontSize: 12, paddingTop: 8 };

const axisProps = (palette) => ({
  stroke: palette.axis,
  tick: { fill: palette.axis, fontSize: 11 },
  tickLine: false,
  axisLine: { stroke: palette.grid },
});

const totalOf = (rows, key) => rows.reduce((sum, row) => sum + toNumber(row[key]), 0);

/* Books can run negative (a loss, an overdrawn bank), so the zero line is
   drawn whenever a series dips below it. Recharts only renders its own child
   types, so this is a flag rather than a wrapper component. */
const dipsBelowZero = (data, keys) => data.some((row) => keys.some((key) => toNumber(row[key]) < 0));

/* ------------------------------------------------------------------ summaries
 * Plain-language text read out instead of the SVG. */

export const incomeSummary = (trend) =>
  `Income ${money(totalOf(trend, "income"))}, expenses ${money(totalOf(trend, "expense"))}, net profit ${money(totalOf(trend, "profit"))} across ${trend.length} periods.`;

export const cashSummary = (trend) =>
  `Cash received ${money(totalOf(trend, "cash_in"))}, cash paid ${money(totalOf(trend, "cash_out"))}, net ${money(totalOf(trend, "cash_net"))}.`;

/* --------------------------------------------------------------------- charts */

/** Income and expenses side by side per period, with net profit as a line on the same money axis. */
export const IncomeExpenseChart = ({ data, palette, height }) => {
  const axis = axisProps(palette);
  const bar = { stroke: palette.surface, strokeWidth: 2, maxBarSize: 22, radius: [4, 4, 0, 0], isAnimationActive: false };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={data} margin={MARGIN}>
        <CartesianGrid vertical={false} stroke={palette.grid} strokeWidth={1} />
        <XAxis dataKey="label" {...axis} minTickGap={12} />
        <YAxis {...axis} width={62} tickFormatter={moneyShort} />
        <Tooltip cursor={{ fill: palette.grid, fillOpacity: 0.6 }} content={<SeriesTooltip />} />
        <Legend wrapperStyle={LEGEND_STYLE} />
        {dipsBelowZero(data, ["income", "expense", "profit"]) && <ReferenceLine y={0} stroke={palette.axis} strokeWidth={1} />}
        <Bar dataKey="income" name="Income" fill={palette.sales} legendType="rect" {...bar} />
        <Bar dataKey="expense" name="Expenses" fill={palette.pending} legendType="rect" {...bar} />
        <Line
          type="monotone"
          dataKey="profit"
          name="Net profit"
          stroke={palette.collection}
          strokeWidth={2}
          legendType="plainline"
          dot={data.length <= 12 ? { r: 4, strokeWidth: 2, stroke: palette.surface, fill: palette.collection } : false}
          activeDot={{ r: 5, strokeWidth: 2, stroke: palette.surface }}
          isAnimationActive={false}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

/** Cash received against cash paid out, per period. */
export const CashFlowChart = ({ data, palette, height }) => {
  const axis = axisProps(palette);
  const bar = { stroke: palette.surface, strokeWidth: 2, maxBarSize: 22, radius: [4, 4, 0, 0], isAnimationActive: false };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={MARGIN}>
        <CartesianGrid vertical={false} stroke={palette.grid} strokeWidth={1} />
        <XAxis dataKey="label" {...axis} minTickGap={12} />
        <YAxis {...axis} width={62} tickFormatter={moneyShort} />
        <Tooltip cursor={{ fill: palette.grid, fillOpacity: 0.6 }} content={<SeriesTooltip />} />
        <Legend iconType="rect" wrapperStyle={LEGEND_STYLE} />
        <Bar dataKey="cash_in" name="Cash in" fill={palette.collection} {...bar} />
        <Bar dataKey="cash_out" name="Cash out" fill={palette.pending} {...bar} />
      </BarChart>
    </ResponsiveContainer>
  );
};
